<script setup lang="ts">
import { onUnmounted, ref, watch, nextTick } from 'vue';
import { X } from '@lucide/vue';

const props = defineProps<{
  open: boolean;
  title?: string;
  /** Подзаголовок под title. */
  subtitle?: string;
  /** max-width окна на десктопе (на телефоне модалка всегда во всю ширину, снизу). */
  width?: string;
  /** Не закрывать по клику на фон (длинные формы, где промах стоил бы введённого). */
  persistent?: boolean;
}>();

const emit = defineEmits<{ (e: 'close'): void }>();

const panel = ref<HTMLElement | null>(null);
let locked = false;

function onKey(e: KeyboardEvent) {
  if (e.key === 'Escape' && props.open) {
    e.stopPropagation();
    emit('close');
  }
}

function lock(on: boolean) {
  if (locked === on) return;
  locked = on;
  // Счётчик — модалки бывают вложенными (пикер ресурса поверх редактора).
  const body = document.body as HTMLElement & { _modalLocks?: number };
  body._modalLocks = Math.max(0, (body._modalLocks ?? 0) + (on ? 1 : -1));
  body.classList.toggle('modal-open', body._modalLocks > 0);
}

watch(
  () => props.open,
  async (v) => {
    if (v) {
      lock(true);
      window.addEventListener('keydown', onKey);
      await nextTick();
      // Фокус в окно — чтобы Tab не убегал на страницу под ним. Поле не фокусируем на
      // телефоне: всплывающая клавиатура закрыла бы половину шторки.
      const touch = window.matchMedia('(pointer: coarse)').matches;
      const first = touch
        ? null
        : panel.value?.querySelector<HTMLElement>('input:not([type=hidden]):not([disabled]):not([type=checkbox]), select, textarea');
      (first ?? panel.value)?.focus({ preventScroll: true });
    } else {
      lock(false);
      window.removeEventListener('keydown', onKey);
    }
  },
  { immediate: true },
);

onUnmounted(() => {
  lock(false);
  window.removeEventListener('keydown', onKey);
});

function onBackdrop() {
  if (!props.persistent) emit('close');
}
</script>

<template>
  <Teleport to="body">
    <Transition name="modal">
      <div v-if="open" class="m-overlay" @mousedown.self="onBackdrop">
        <div
          ref="panel"
          class="m-panel"
          role="dialog"
          aria-modal="true"
          tabindex="-1"
          :style="{ maxWidth: width ?? '640px' }"
        >
          <header v-if="title || subtitle || $slots.header" class="m-header">
            <div class="m-heading">
              <slot name="header">
                <h3 class="m-title">{{ title }}</h3>
                <p v-if="subtitle" class="m-subtitle">{{ subtitle }}</p>
              </slot>
            </div>
            <button class="btn btn-ghost btn-icon btn-sm m-close" aria-label="Close" @click="emit('close')">
              <X />
            </button>
          </header>
          <div class="m-body">
            <slot />
          </div>
          <footer v-if="$slots.footer" class="m-footer">
            <slot name="footer" />
          </footer>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
.m-overlay {
  position: fixed;
  inset: 0;
  z-index: 100;
  background: var(--c-overlay);
  backdrop-filter: blur(3px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
}
.m-panel {
  width: 100%;
  max-height: calc(100dvh - 48px);
  display: flex;
  flex-direction: column;
  background: var(--c-surface);
  border: 1px solid var(--c-border);
  border-radius: var(--radius-xl);
  box-shadow: var(--shadow-lg);
  outline: none;
  overflow: hidden;
}
.m-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 18px 20px 14px;
  border-bottom: 1px solid var(--c-border-soft);
}
.m-heading { min-width: 0; }
.m-title, .m-header :deep(h3) {
  margin: 0;
  font-size: var(--fs-lg);
  font-weight: 650;
  color: var(--c-text);
}
.m-subtitle { margin-top: 2px; font-size: var(--fs-sm); color: var(--c-text-2); }
.m-close { margin: -4px -6px 0 0; }
.m-body { flex: 1; overflow-y: auto; padding: 18px 20px; overscroll-behavior: contain; }
.m-footer {
  display: flex;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 8px;
  padding: 14px 20px;
  border-top: 1px solid var(--c-border-soft);
  background: var(--c-surface-2);
}
.m-footer :deep(.btn-group) { margin-top: 0; }

.modal-enter-active, .modal-leave-active { transition: opacity 0.18s var(--ease); }
.modal-enter-active .m-panel, .modal-leave-active .m-panel { transition: transform 0.2s var(--ease), opacity 0.2s var(--ease); }
.modal-enter-from, .modal-leave-to { opacity: 0; }
.modal-enter-from .m-panel, .modal-leave-to .m-panel { transform: translateY(8px) scale(0.98); opacity: 0; }

@media (max-width: 640px) {
  .m-overlay { padding: 0; align-items: flex-end; }
  .m-panel {
    max-width: 100% !important;
    max-height: 94dvh;
    border-radius: var(--radius-xl) var(--radius-xl) 0 0;
    border-bottom: none;
  }
  .m-panel::before {
    content: '';
    display: block;
    width: 36px;
    height: 4px;
    border-radius: 4px;
    background: var(--c-border-strong);
    margin: 8px auto 0;
    flex-shrink: 0;
  }
  .m-header { padding: 10px 16px 12px; }
  .m-body { padding: 16px; }
  .m-footer { padding: 12px 16px calc(12px + env(safe-area-inset-bottom)); }
  .m-footer > :deep(.btn) { flex: 1 1 auto; }
  .modal-enter-from .m-panel, .modal-leave-to .m-panel { transform: translateY(100%); opacity: 1; }
}
</style>
